import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { useToast } from '../../../components/ui/use-toast';
import { SearchableSelect } from '../../../components/ui/searchable-select';
import { FileSpreadsheet, FileText, Download, Eye, X } from 'lucide-react';

interface GroupOption {
  id: number;
  groupName: string;
  groupCode: string;
}

type ReportKey = 'calendario-resumido' | 'firmas-entrega' | 'lista-asistencia';

const REPORTS: Array<{ key: ReportKey; label: string; description: string; icon: typeof FileSpreadsheet; filenamePrefix: string }> = [
  { key: 'calendario-resumido', label: 'Calendario Resumido', description: 'Fechas de inicio/fin por materia, feriados y vacaciones del grupo.', icon: FileSpreadsheet, filenamePrefix: 'calendario-resumido' },
  { key: 'firmas-entrega', label: 'Firmas de Entrega de Calificaciones', description: 'Checklist por materia para que cada profesor firme la entrega de listas.', icon: FileText, filenamePrefix: 'firmas-entrega' },
  { key: 'lista-asistencia', label: 'Lista de Asistencia', description: 'Un libro con una hoja por materia: roster, sesiones y calificaciones del grupo.', icon: FileSpreadsheet, filenamePrefix: 'lista-asistencia' },
];

export default function Reportes() {
  const { toast } = useToast();
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<number | null>(null);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [downloading, setDownloading] = useState<ReportKey | null>(null);
  const [previewing, setPreviewing] = useState<ReportKey | null>(null);
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);
  const [previewTitle, setPreviewTitle] = useState<string>('');

  useEffect(() => {
    fetchGroups();
  }, []);

  const fetchGroups = async () => {
    try {
      const response = await fetch('/api/attendance/groups', { credentials: 'include' });
      if (response.status === 403) {
        toast({
          title: 'Permiso insuficiente',
          description: 'Tu rol no tiene permiso para listar grupos (company:read). Pídeselo a un administrador.',
          variant: 'destructive',
        });
        return;
      }
      if (!response.ok) throw new Error('Error al cargar grupos');
      const data = await response.json();
      setGroups(data);
    } catch (err) {
      toast({ title: 'Error', description: 'No se pudieron cargar los grupos', variant: 'destructive' });
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleDownload = async (report: typeof REPORTS[number]) => {
    if (!selectedGroupId) {
      toast({ title: 'Selecciona un grupo', description: 'Elige un grupo antes de descargar', variant: 'destructive' });
      return;
    }
    setDownloading(report.key);
    try {
      const response = await fetch(`/api/reportes/${report.key}?periodId=${selectedGroupId}`, { credentials: 'include' });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || 'Error al generar el reporte');
      }
      const blob = await response.blob();
      const group = groups.find((g) => g.id === selectedGroupId);
      const ext = report.key === 'firmas-entrega' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${report.filenamePrefix}-${group?.groupCode || selectedGroupId}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Error al descargar el reporte', variant: 'destructive' });
    } finally {
      setDownloading(null);
    }
  };

  const handlePreview = async (report: typeof REPORTS[number]) => {
    if (!selectedGroupId) {
      toast({ title: 'Selecciona un grupo', description: 'Elige un grupo antes de previsualizar', variant: 'destructive' });
      return;
    }
    setPreviewing(report.key);
    try {
      const response = await fetch(`/api/reportes/${report.key}/preview?periodId=${selectedGroupId}`, { credentials: 'include' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Error al generar la vista previa');
      setPreviewTitle(report.label);
      setPreviewHtml(data.html);
    } catch (err: any) {
      toast({ title: 'Error', description: err.message || 'Error al generar la vista previa', variant: 'destructive' });
    } finally {
      setPreviewing(null);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <h1 className="text-2xl font-bold">Reportes Institucionales</h1>

      <div className="max-w-md">
        <label className="block text-sm font-medium mb-1">Grupo</label>
        <SearchableSelect
          options={groups.map((g) => ({ value: g.id, label: `${g.groupCode} — ${g.groupName}` }))}
          value={selectedGroupId}
          onChange={(v) => setSelectedGroupId(v === null ? null : Number(v))}
          placeholder={loadingGroups ? 'Cargando grupos...' : 'Buscar grupo...'}
          disabled={loadingGroups}
        />
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {REPORTS.map((report) => (
          <div key={report.key} className="border rounded-lg p-4 flex flex-col gap-3">
            <report.icon className="w-6 h-6 text-muted-foreground" />
            <div>
              <h2 className="font-semibold">{report.label}</h2>
              <p className="text-sm text-muted-foreground">{report.description}</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => handlePreview(report)}
                disabled={!selectedGroupId || previewing === report.key}
                variant="ghost"
                className="flex-1"
              >
                <Eye className="w-4 h-4 mr-2" />
                {previewing === report.key ? 'Cargando...' : 'Vista previa'}
              </Button>
              <Button
                onClick={() => handleDownload(report)}
                disabled={!selectedGroupId || downloading === report.key}
                variant="outline"
                className="flex-1"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloading === report.key ? 'Generando...' : 'Descargar'}
              </Button>
            </div>
          </div>
        ))}
      </div>

      {previewHtml !== null && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setPreviewHtml(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b p-4">
              <h2 className="text-lg font-semibold">Vista previa — {previewTitle}</h2>
              <Button variant="ghost" size="icon" onClick={() => setPreviewHtml(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
            <div className="overflow-auto p-4" dangerouslySetInnerHTML={{ __html: previewHtml }} />
          </div>
        </div>
      )}
    </div>
  );
}
